<template>
  <v-stepper
    data-test-id="importView"
    ref="importStepper"
    :mobile="isMobile"
    v-model="store.currentStep"
    :items="steps"
    :alt-labels="true"
  >
    <template v-slot:item.1>
      <v-container fluid>
        <v-row>
          <v-col class="column">
            <p v-if="isMobile" class="step-title">{{ store.getCurrentTitle() }}</p>
            <p class="stepper-instructions">
              {{ t('text.import.instructions1') }}
            </p>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col class="column">
            <PrimarySelect
              max-width="200"
              v-model="store.selectedImportDefinition"
              :label="t('labels.table')"
              :items="tableOptions"
            ></PrimarySelect>
            <p>
              <strong
                >{{ t('text.import.guidance') }}
                <a target="_blank" href="https://tsi.com/tsilink-respro/importing">{{
                  t('labels.here')
                }}</a
                >.
              </strong>
            </p>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template v-slot:item.2>
      <v-container fluid>
        <v-row>
          <v-col class="column">
            <p v-if="isMobile" class="step-title">{{ store.getCurrentTitle() }}</p>
            <p class="stepper-instructions">
              {{ t('text.import.instructions2') }}
            </p>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col class="column">
            <FileInput
              style="margin-right: 1rem"
              v-model="store.selectedFile"
              :max-width="300"
              :label="t('labels.openFilePicker')"
              accept=".csv, text/csv, text/plain"
              @update:model-value="store.readFile"
              @click:clear="store.clearFile"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="column">
            <p v-if="store.parsedFileData.length > 0 && store.parsedFileErrors.length === 0">
              {{ store.parsedFileInfo }}&nbsp;
            </p>
            <p class="error-message" v-else-if="store.parsedFileErrors.length > 0">
              {{ store.parsedFileInfo }}
              <v-data-table
                v-if="store.parsedFileErrors.length > 0"
                class="import-table"
                :headers="parseFileErrorHeaders"
                density="compact"
                :items="store.parsedFileErrors"
                :items-per-page="10"
                :items-per-page-options="settingsStore.itemsPerPageOptions"
              >
              </v-data-table></p
          ></v-col>
        </v-row>
      </v-container>
    </template>

    <template v-slot:item.3>
      <v-container fluid>
        <v-row style="margin-bottom: 1rem">
          <v-col class="column">
            <p v-if="isMobile" class="step-title">{{ store.getCurrentTitle() }}</p>
            <p class="stepper-instructions" style="margin-bottom: 0">
              <strong> {{ t('labels.table') }}: {{ friendlyTableName }} </strong>
            </p>
            <p class="stepper-instructions" style="margin-bottom: 0">
              {{ t('text.import.instructions3a') }}
              <span v-if="store.selectedImportDefinition?.endpoint === EndPoint.CustomFields">
                {{
                  t('info.importCustomFields', {
                    tableName: friendlyTableName
                  })
                }}
              </span>
            </p>
            <p
              v-if="store.missingFields?.length > 0"
              class="stepper-error"
              style="margin-bottom: 0"
            >
              {{ t('errors.missingFields', { fieldNames: store.missingFields.join(', ') }) }}
            </p>
            <p v-if="store.skipCount > 0" class="stepper-warning" style="margin-bottom: 0">
              {{ t('warnings.importWarning') }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="column">
            <v-btn-toggle
              :disabled="store.selectedImportDefinition?.allowInsert === false"
              class="toggle-button"
              v-model="store.importMode"
              density="compact"
              color="primary"
              rounded="8"
              mandatory
              variant="outlined"
            >
              <v-btn :value="ImportMode.Insert">{{ t('buttons.insert') }}</v-btn>
              <v-btn :value="ImportMode.Overwrite">{{ t('buttons.overwrite') }}</v-btn>
            </v-btn-toggle>
            <span v-if="store.importMode === ImportMode.Insert">{{
              t('text.import.instructions3b')
            }}</span>
            <span v-if="store.importMode === ImportMode.Overwrite">{{
              t('text.import.instructions3c')
            }}</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              class="import-table"
              v-if="store.selectedImportFields.length > 0"
              density="compact"
              :headers="store.selectedImportFields"
              :items="store.parsedFileData"
              :items-per-page="10"
              :items-per-page-options="settingsStore.itemsPerPageOptions"
            >
              <template
                v-for="(item, index) in store.selectedImportFields"
                v-slot:[`header.[${index}]`]
                :key="index"
              >
                <PrimarySelect
                  v-if="!item.ignore"
                  class="header-select"
                  density="compact"
                  :hide-details="true"
                  min-width="200"
                  v-model="store.selectedFields[index]"
                  :items="store.getFieldOptions(index)"
                ></PrimarySelect>
                <span class="ignore-label" v-if="item.ignore">
                  <strong>{{ item.title }}</strong>
                </span>
              </template>

              <template v-slot:item="{ index: rowIndex }">
                <tr class="v-data-table__tr">
                  <td
                    v-for="importField in store.selectedImportFields"
                    :key="importField.index"
                    class="v-data-table__td v-data-table-column--align-start"
                  >
                    {{ getParsedValue(rowIndex, importField) }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-slot:item.4>
      <v-container fluid>
        <v-row>
          <v-col class="column">
            <p v-if="isMobile" class="step-title">{{ store.getCurrentTitle() }}</p>
            <p class="stepper-instructions" style="margin-bottom: 0">
              {{ t('text.import.instructions4') }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <p class="stepper-instructions" style="margin-bottom: 0">
              <strong>{{ t('labels.table') }}:</strong>
              {{ friendlyTableName }}
            </p>
            <p class="stepper-instructions" style="margin-bottom: 0">
              <strong>{{ t('labels.importMode') }}:</strong> {{ t(`buttons.${store.importMode}`) }}
            </p>
            <p class="stepper-instructions" style="margin-bottom: 0">
              <strong>{{ t('labels.totalRecords') }}:</strong> {{ store.totalRecords }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <ButtonPrimary
              v-if="store.importState === ImportState.Ready"
              :disabled="store.isImporting"
              @click="store.importFile()"
              :text="t('buttons.import')"
            />&nbsp;
            <ButtonSecondary
              :disabled="!store.isImporting"
              @click="store.cancelImport()"
              :text="t('buttons.cancel')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <strong>{{ t('labels.progress') }}: {{ store.importProgress }}%</strong>
            <v-progress-linear
              color="primary"
              height="8"
              v-model:model-value="store.importProgress"
              :striped="store.isImporting"
            ></v-progress-linear>
          </v-col>
        </v-row>
        <v-row v-if="isDone" style="padding-top: 0; margin-top: 0">
          <v-col style="padding-top: 0; margin-top: 0">
            <p class="stepper-instructions" style="margin-bottom: 0">
              {{ store.totalInserted }} {{ t('labels.totalInserted') }}
              <span v-if="store.importMode === ImportMode.Overwrite">
                , {{ store.totalOverwritten }} {{ t('labels.totalOverwritten') }}
              </span>
              <span v-if="store.totalErrors > 0">
                , {{ store.totalErrors }} {{ t('labels.totalErrors') }}
              </span>
            </p>
          </v-col>
        </v-row>
        <v-row v-if="store.errorMessages.length > 0" style="margin: 0; padding: 0">
          <v-col style="margin: 0; padding: 0">
            <v-divider style="margin: 1rem 0 1rem 0" />
            <p class="stepper-instructions">
              <span class="error-message">{{ t('errors.import') }}</span>
            </p>
            <p>
              <v-data-table
                class="import-table"
                :headers="importErrorHeaders"
                density="compact"
                :items="store.errorMessages"
                :items-per-page="10"
                :items-per-page-options="settingsStore.itemsPerPageOptions"
              />
            </p>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-slot:actions>
      <v-container fluid>
        <v-row v-if="store.importState === ImportState.Ready">
          <v-col align="left" class="column">
            <ButtonSecondary
              v-if="store.currentStep > 1"
              :disabled="store.isImporting"
              @click="importStepper.prev()"
              density="compact"
              :text="t('buttons.previous')"
            />
          </v-col>
          <v-col align="right" class="column">
            <ButtonPrimary
              v-if="store.currentStep < store.steps.length"
              :disabled="!hasCompleted"
              @click="importStepper.next()"
              density="compact"
              :text="t('buttons.next')"
            />
          </v-col>
        </v-row>
        <v-row
          v-if="
            store.importState === ImportState.Cancelled ||
            store.importState === ImportState.Completed
          "
        >
          <v-col align="left" class="column">
            <ButtonPrimary @click="store.resetImport" density="compact" :text="t('buttons.done')" />
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-stepper>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { EndPoint } from '@/enums/EndPoint'
import { ImportMode } from '@/enums/ImportMode'
import { ImportState } from '@/enums/ImportState'
import type { IImportDefinition } from '@/interfaces/IImportDefinition'
import type { IImportFieldDefinition } from '@/interfaces/IImportFieldDefinition'
import type { ITableHeader } from '@/interfaces/ITableHeader'
import { useImportStore } from '@/stores/import/ImportStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay, type DisplayInstance } from 'vuetify'

const display: DisplayInstance = useDisplay()
const { t } = useI18n({ useScope: 'global' })
const settingsStore = useSettingsStore()
const store = useImportStore()

// PROPS & EMITS

// REACTIVE VARIABLES
const importStepper = ref()

// COMPUTED PROPERTIES
const friendlyTableName = computed(() => {
  if (store.selectedImportDefinition) {
    return t(store.selectedImportDefinition.friendlyName)
  }
  return ''
})

const hasCompleted = computed(() => {
  if (store.currentStep === 3) {
    return store.missingFields?.length === 0
  } else {
    return store.steps[store.currentStep - 1].hasCompleted
  }
})

const importErrorHeaders = computed(() => {
  return store.importErrorHeaders.map((header: ITableHeader) => {
    return { ...header, title: t(header.title!) }
  })
})

const isDone = computed(() => {
  return store.importState === ImportState.Cancelled || store.importState === ImportState.Completed
})

const isMobile = computed(() => {
  return display.width.value <= 675
})

const parseFileErrorHeaders = computed(() => {
  return store.parseFileErrorHeaders.map((header: ITableHeader) => {
    return { ...header, title: t(header.title!) }
  })
})

const steps = computed(() => {
  return store.steps.map((step) => {
    return {
      title: t(step.title),
      hasCompleted: step.hasCompleted
    }
  })
})

const tableOptions = computed(() => {
  return store.importDefinitions.map((importDefinition: IImportDefinition) => {
    return { title: t(importDefinition.friendlyName), value: importDefinition }
  })
})
// LIFECYCLE HOOKS

// FUNCTIONS
const getParsedValue = (rowIndex: number, importField: IImportFieldDefinition) => {
  const rowData = store.parsedFileData[rowIndex]
  let value
  if (rowData) {
    value = rowData[importField.index!]
  }
  if (value === null) {
    return '(NULL)'
  }
  if (value === '') {
    return '(EMPTY)'
  }
  if (value === undefined) {
    return '(undefined)'
  }

  if (importField.display) {
    return importField.display(value)
  } else {
    return value
  }
}
</script>

<style lang="scss" scoped>
.column {
  padding: 0;
  margin: 0;
}

.ignore-label {
  min-width: 200px;
  padding: 0;
  margin: 0;
}

.stepper-instructions {
  margin: 0 0 1rem 0;
  padding: 0;
}

.stepper-error {
  margin: 0 0 1rem 0;
  padding: 0;
  color: $color-accent-error;
}

.step-title {
  margin-top: 0;
  padding-top: 0;
  font-weight: bold;
}

.stepper-warning {
  margin: 0 0 1rem 0;
  padding: 0;
  color: $color-accent-primary;
}

.error-message {
  color: $color-accent-error;
}

.header-select {
  padding: 0;
  margin: 0;
}

.header-action {
  padding: 0;
  margin: 0.55rem 0 0 0;
  min-width: 150px;
}

.import-table {
  vertical-align: top;
}

.toggle-button {
  margin-right: 1rem;
}
</style>
